import { isBillingTierEnabled } from '@civilgrid/utils/src/common/featureAccess';
import Dinero from 'dinero.js';
import type React from 'react';
import { useMemo } from 'react';
import FeatureFlags, { OrgFeatureFlag } from '~/authorizations/FeatureFlags';
import { billingTierLabels } from '~/common/projectUtils';
import type { Subscription, SubscriptionProjectType } from '~/common/types';

const BILLING_TIER_HIERARCHY = ['draft', 'proposal', 'project', 'projectPlus'];

function getAvailableProjectTypes({ subscription }: { subscription: Subscription }) {
  const { projectCost } = subscription;
  const availableProjectTypes: (keyof (typeof projectCost)['project'])[] = [];

  // filter out intersection type manually so that the default still gets sent to the backend
  const types = Object.keys(projectCost['project']).filter((t) => t !== 'intersection');

  types.forEach((_type) => {
    const t = _type as keyof (typeof projectCost)['project'];

    // only show circle projects if the circle projects feature flag is enabled
    if (t === 'circle') {
      if (FeatureFlags.hasOrgFeatureFlag(OrgFeatureFlag.CircleProjects)) {
        availableProjectTypes.push(t);
      }
    } else {
      availableProjectTypes.push(t);
    }
  });

  return availableProjectTypes;
}

function getPlurality(unit: number): string {
  return unit === 1 ? '' : 's';
}

export default function PricingTable(props: { subscription: Subscription }) {
  const { subscription } = props;

  const allEqual = useMemo<boolean>(() => {
    return Object.values(subscription.projectCost).every((tier) => {
      return Object.values(tier).every((type: SubscriptionProjectType) => {
        return type.urban.cost === type.rural.cost && type.urban.unit === type.rural.unit;
      });
    });
  }, [subscription]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const tableBody = useMemo(() => {
    const rows: React.ReactNode[] = [];

    const { projectCost } = subscription;
    const availableProjectTypes = getAvailableProjectTypes({ subscription });

    const billingTiers = (Object.keys(projectCost) as (keyof typeof projectCost)[])
      .filter((tier) => isBillingTierEnabled(tier, subscription))
      .sort((a, b) => BILLING_TIER_HIERARCHY.indexOf(a) - BILLING_TIER_HIERARCHY.indexOf(b));

    billingTiers.forEach((_tier) => {
      const tier = _tier as keyof typeof projectCost;
      const tierConfig = projectCost[tier];

      availableProjectTypes.forEach((type, i) => {
        const projectTypeConfig = tierConfig[type as keyof typeof tierConfig];
        const urbanCost = Dinero({ amount: projectTypeConfig.urban.cost });
        const ruralCost = Dinero({ amount: projectTypeConfig.rural.cost });
        const tierLabel = billingTierLabels[tier as keyof typeof billingTierLabels];

        let content: React.ReactNode = null;

        if (allEqual) {
          content = (
            <tr key={`${tier}-${type}`} className="text-sm">
              <td className="p-1 capitalize">{i === 0 ? tierLabel : ''}</td>
              <td className="p-1 capitalize">{type}</td>
              <td className="p-1">
                <span>{urbanCost.toFormat()}</span> /{' '}
                <span>
                  {projectTypeConfig.urban.unit}{' '}
                  {type === 'linear'
                    ? `mile${getPlurality(projectTypeConfig.urban.unit)}`
                    : `acre${getPlurality(projectTypeConfig.urban.unit)}`}
                </span>
              </td>
            </tr>
          );
        } else {
          content = (
            <tr key={`${tier}-${type}`}>
              <td className="p-1 capitalize">{i === 0 ? tierLabel : ''}</td>
              <td className="p-1 capitalize">{type}</td>
              <td className="p-1">
                <span>{urbanCost.toFormat()}</span> /{' '}
                <span>
                  {projectTypeConfig.urban.unit}{' '}
                  {type === 'linear'
                    ? `mile${getPlurality(projectTypeConfig.urban.unit)}`
                    : `acre${getPlurality(projectTypeConfig.urban.unit)}`}
                </span>
              </td>
              <td className="p-1">
                <span>{ruralCost.toFormat()}</span> /{' '}
                <span>
                  {projectTypeConfig.rural.unit}{' '}
                  {type === 'linear'
                    ? `mile${getPlurality(projectTypeConfig.rural.unit)}`
                    : `acre${getPlurality(projectTypeConfig.rural.unit)}`}
                </span>
              </td>
            </tr>
          );
        }

        rows.push(content);
      });
    });

    return rows;
  }, [allEqual, subscription.projectCost]);

  // don't show the table if perProjectCost is not available
  if (subscription.projectCost === undefined) {
    return null;
  }

  return (
    <div className="space-y-14">
      <div>
        <div className="pl-1 text-base font-bold">Pricing</div>
        <div className="pl-1 mb-4 text-sm">
          For billing or pricing related questions, please email support@civilgrid.com
        </div>
        <table className="w-full p-1">
          <thead className="text-left text-sm font-bold">
            <tr>
              <th className="p-1">Project Tier</th>
              <th className="p-1">Project Type</th>
              {allEqual ? (
                <th className="p-1">Cost / Unit</th>
              ) : (
                <>
                  <th className="p-1">Urban</th>
                  <th className="p-1">Rural</th>
                </>
              )}
            </tr>
          </thead>

          <tbody>{tableBody}</tbody>
        </table>
      </div>
    </div>
  );
}
